/**
 * HelpTab — extracted from /app/frontend/src/pages/PalGame.jsx
 * as part of the per-tab split. Behaviour identical to the
 * pre-split monolith; only the file boundary changed.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, RarityBadge } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { PayoutFlash } from "@/components/PayoutFlash";
import { AchievementUnlocked } from "@/components/AchievementUnlocked";
import { MasterTrainerCinematic } from "@/components/MasterTrainerCinematic";
import { useMasterTrainers } from "@/hooks/useMasterTrainers";
import {
  Swords, Sparkles, Zap, Compass, Trophy, Layers, Target, Castle,
  ArrowLeftRight, Map, Crosshair, Heart, ChevronLeft, BookOpen,
} from "lucide-react";
import { PalPicker, ActionWithPal, firePalgamePayout, firePalgameDefeat } from "./_shared";

export function HelpTab() {
  const sections = [
    {
      id: "catch",
      title: "Catch",
      icon: Target,
      body: (
        <>
          Each catch consumes one of your spheres. Higher-tier spheres
          (Mega, Hyper, Ultra) raise your odds against rarer pals.
          <ul className="list-disc pl-5 mt-2 space-y-1 text-[var(--muted)] text-xs">
            <li>Rarity scales the catch chance — Common pals are near-guaranteed; Legendaries are a tough roll even with Ultra spheres.</li>
            <li>Every catch awards skulls and rolls three random talents (0–100) for the pal.</li>
            <li>Guaranteed passives apply on catch (Cattiva → Coward, Jetragon → Legend + Divine Dragon, etc.). Remaining slots roll random rank-weighted passives.</li>
            <li><span className="text-yellow-300">✨ Lucky pals</span> appear at roughly 1 in 512 catches. They auto-equip the &quot;Lucky&quot; passive and get a golden ring everywhere on the site.</li>
            <li>60-minute cooldown between catches.</li>
          </ul>
        </>
      ),
    },
    {
      id: "box",
      title: "Pal Box",
      icon: Layers,
      body: (
        <>
          Your collection. Sort by level, rarity, or recency. Click any pal to open the detail card.
          <ul className="list-disc pl-5 mt-2 space-y-1 text-[var(--muted)] text-xs">
            <li>Stat block shows the in-game-accurate HP / ATK / DEF — same formula as the Builder page.</li>
            <li>Talents (IVs) are 0–100. Raise them with Life / Power / Stout Fruit on the <strong>Pal Care</strong> tab.</li>
            <li>Passives panel shows up to 4 with rank-arrow icons + effect chips.</li>
            <li>&quot;Type Matchups&quot; lists what your pal counters and what counters it.</li>
          </ul>
        </>
      ),
    },
    {
      id: "breed",
      title: "Breed",
      icon: Zap,
      body: (
        <>
          Pick two parents from your box and consume them to roll a new
          offspring. Combinations follow Palworld&apos;s breeding-power chart
          plus ~30 unique combos (Anubis + Frostallion → Bastigor, etc.).
          The offspring inherits passives from the union of both parents&apos;
          pools — 50% per unique slot up to 4, with a 15% mutation chance
          for one fresh trait. Talents are re-rolled (in-game faithful).
          Lucky parents quadruple the offspring-Lucky odds. Note that
          species-locked passives (Legend, Lurker, Invader, etc.) only
          come from catching the apex pal directly — they don&apos;t auto-
          apply on bred offspring.
          For a free preview of any combo (including pals you don&apos;t own),
          use the dedicated Breeding Calculator page.
        </>
      ),
    },
    {
      id: "care",
      title: "Pal Care — Talent Fruits & Surgery",
      icon: Sparkles,
      body: (
        <>
          The on-site pal hospital. Three services, one tab (between Breed and Adventure).
          <ul className="list-disc pl-5 mt-2 space-y-1 text-[var(--muted)] text-xs">
            <li><strong>Life Fruit</strong> → +10 talent HP. <strong>Power Fruit</strong> → +10 ATK. <strong>Stout Fruit</strong> → +10 DEF. Cap at 100.</li>
            <li>Buy with Chillet Coins (1 500 each) OR consume drops you&apos;ve earned from battles, towers, arena, PvP, expeditions.</li>
            <li><strong>Random Surgery</strong> (5 000 coins) re-rolls every unlocked passive on a pal.</li>
            <li><strong>Add Passive</strong> (10 000 coins) appends one new passive without touching the existing ones.</li>
            <li><strong>Targeted Swap</strong> (15 000 coins) replaces one specific passive with any other.</li>
            <li>Locked passives (guaranteed ones + Lucky-pal Lucky) never get touched.</li>
          </ul>
        </>
      ),
    },
    {
      id: "adventure",
      title: "Adventure",
      icon: Compass,
      body: (
        <>
          Send a pal on a solo run for random skulls + XP. Lower risk than Battle,
          higher base than Catch. 60-minute cooldown.
        </>
      ),
    },
    {
      id: "expedition",
      title: "Expedition",
      icon: Map,
      body: (
        <>
          Two-pal teams head out on a longer run for bigger payouts.
          12% chance to bring back a Life / Power / Stout Fruit drop.
          Pals come back &quot;tired&quot; for the next slot — pick fresh ones each time.
        </>
      ),
    },
    {
      id: "trade",
      title: "Trade",
      icon: ArrowLeftRight,
      body: (
        <>
          List one of your pals + a sphere/coin price — anyone in the community can pick it up.
          Trades are anonymous until accepted. 24-hour expiry.
        </>
      ),
    },
    {
      id: "battle",
      title: "Battle",
      icon: Swords,
      body: (
        <>
          Pick a pal from your box and fight either a level-scaled wild pal or one of the
          9 NPC trainers. Turn-based — pick a skill each turn from your pal&apos;s learnset.
          <ul className="list-disc pl-5 mt-2 space-y-1 text-[var(--muted)] text-xs">
            <li>HP / damage all use the in-game formula — your stat card numbers are exactly what the battle bar shows.</li>
            <li>Wild win → ~6 % chance to drop a fruit. Trainer win → ~18 %.</li>
            <li>Losing is free — no cooldown, no penalty. Try again immediately.</li>
            <li>Clear all 9 trainers in a row to earn the <span className="text-yellow-300">Master Trainer</span> badge.</li>
          </ul>
        </>
      ),
    },
    {
      id: "arena",
      title: "Arena",
      icon: Castle,
      body: (
        <>
          Tiered team battles — Bronze, Silver, Gold, Diamond, and Legendary. Pay an entry fee,
          pick a 3-pal team that meets the tier&apos;s level requirement, and roll for the win.
          <ul className="list-disc pl-5 mt-2 space-y-1 text-[var(--muted)] text-xs">
            <li>Win odds scale with your team&apos;s total levels vs the tier&apos;s expected baseline.</li>
            <li>Wins pay out 2–10× the entry fee plus shared XP across the 3 pals.</li>
            <li>~25 % chance of a fruit drop per arena win.</li>
          </ul>
        </>
      ),
    },
    {
      id: "pvp",
      title: "PvP",
      icon: Crosshair,
      body: (
        <>
          Challenge another player to a wager match. Both sides put up skulls + commit a pal;
          the battle resolves instantly with the same engine the wild/trainer battles use.
          ~30 % fruit drop on victory. Challenges expire in 24 h if not answered.
        </>
      ),
    },
    {
      id: "leaderboard",
      title: "Top",
      icon: Trophy,
      body: (
        <>
          Site-wide leaderboards across multiple metrics — total skulls, pal collection size,
          arena/PvP wins, trainer clears, etc. Master Trainers get a permanent badge.
        </>
      ),
    },
  ];

  return (
    <div className="max-w-4xl mx-auto fade-up space-y-3" data-testid="palgame-help">
      <div className="glass-card p-6">
        <div className="flex items-start gap-3 mb-4">
          <BookOpen size={28} className="text-[var(--teal)]" />
          <div>
            <h3 className="text-2xl mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
              How the Pal Game works
            </h3>
            <p className="text-sm text-[var(--muted)]">
              Quick rundown of every tab on this page. Each section
              maps to the matching tab above — including the Pal Care
              services (talent fruits + surgery table).
            </p>
          </div>
        </div>
      </div>

      {sections.map(({ id, title, icon: Icon, body }) => (
        <details
          key={id}
          className="glass-card group transition-colors"
          data-testid={`palgame-help-${id}`}
        >
          <summary className="flex items-center gap-3 select-none list-none p-4 hover:bg-white/[0.03] transition-colors rounded-[inherit]">
            <Icon size={18} className="text-[var(--teal)] flex-shrink-0" />
            <span className="text-sm uppercase tracking-[0.18em] flex-1">{title}</span>
            <span className="text-[10px] text-[var(--muted)] group-open:rotate-90 transition-transform">▶</span>
          </summary>
          <div className="px-4 pb-4 pl-11 text-sm leading-relaxed">{body}</div>
        </details>
      ))}
    </div>
  );
}

